import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Input } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchDifficultyLevelTableColumnNames, fetchAllDifficultyLevel, createDifficultyLevel, updateDifficultyLevel, 
    deleteDifficultyLevel, activateDeactivateDifficultyLevel, updateUserPreference,
    fetchTranslation} from "../../../slices/thunks";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AddModal from "../../../Components/Common/Modal/AddModal";
import AddCommonForm2 from "../../../Components/Common/AddCommonForm/AddCommonForm2";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import AddCommonForm from "../../../Components/Common/AddCommonForm/AddCommonForm";
import { ErrorMessage } from "formik";
import usePermissions from "../../../Components/Hooks/usePermissions";

const labels = [
  {
    id: 1,
    labelName: "Name English",
    fieldName: "text-box-translate",
    name: "NameEn",
    value: "NameEn",
    required: true,
    translateTo: "NameAr", 
    errorMessage: "Please Enter Name In English"
  },
  {
    id: 2,
    labelName: "Name Arabic",
    fieldName: "text-box",
    name: "NameAr",
    value: "NameAr",
    required: true,
    errorMessage: "Please Enter Name In Arabic"
  },
  {
    id: 3, 
    labelName: "Select Color Code",
    fieldName: "color-picker",
    name: "ColorCode",
    value: "ColorCode",
  }
]

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function DifficultyLevel () {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const user = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [lastTranslatedField, setLastTranslatedField] = useState(null); 


  const { DifficultyLevel, tableColumnNames, loading, Headerload, module, translationLoading } = useSelector((state) => ({
    DifficultyLevel: state.DifficultyLevel.difficultyLevel,
    tableColumnNames: state.DifficultyLevel.tableColumnNames,
    loading: state.DifficultyLevel.loading,
    Headerload: state.DifficultyLevel.Headerload,
    module: state.Module.module,
    translationLoading: state.Module.translationLoading, 
  }));


  // Compute permission
  const permission = (() => {
    return usePermissions(3, 14);
  })();

  const onAddClickHandler = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (difficultyLevel, value) => {
    setSelectedRow(difficultyLevel);
    setIsAddOrEdit(value);
    setShow(true);
  };
  
  
  
  // Updated onSubmitHandler to accept Formik values and formikBag
  const onSubmitHandler = (values, { resetForm }) => {
    // e.preventDefault(); // Not needed with Formik

    const difficultyLevelObj = {
      Type: 1,
      NameEn: values.NameEn?.trim() || "", 
      NameAr: values.NameAr?.trim() || "", 
      ColorCode: values.ColorCode || "#000000",
    };

    if (isAddOrEdit === "isEdit") {
        difficultyLevelObj.ID = selectedRow?.ID;
    }

    isAddOrEdit === "isAdd" ?
      dispatch(createDifficultyLevel(difficultyLevelObj)) :
      dispatch(updateDifficultyLevel(difficultyLevelObj));
      
    resetForm();
    setShow(false); // Close the modal
  };

  const handleSave = async () => {
    const preferencesPayload = {
      UserId: user.UserId,
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ColumnId] = selectedColumns.includes(column.ColumnId);
        return acc;
      }, {}),
    };

    setModalOpen(false);

    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(fetchDifficultyLevelTableColumnNames({module: "DIFFICULTYLEVEL"}));      

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteDifficultyLevel({ Ids: selectedIds, UserId: user.UserId }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

   const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
      toast.error(t("EnterTextToTranslate"));
      return;
    }
    setLastTranslatedField(sourceFieldName); // Set the field being translated

    dispatch(fetchTranslation({ LangText: textToTranslate }))
      .unwrap() 
      .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
      })
      .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
      })
      .finally(() => {
        setLastTranslatedField(null); // Reset after completion or error
      });
  };

  const onActiveOrDeactiveChange = (difficultyLevel, e) => {
    dispatch(activateDeactivateDifficultyLevel({ ID: difficultyLevel.ID, IsActive: difficultyLevel.IsActive, UserId: user.UserId }));
  };

  useEffect(() => {
    dispatch(fetchDifficultyLevelTableColumnNames({ module: "DIFFICULTYLEVEL" }))
    dispatch(fetchAllDifficultyLevel({ Type: 1 }));

  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t('Difficulty')} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
                  <Row className="g-4">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => onAddClickHandler("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                {loading || Headerload ? (
                  <div className="d-flex justify-content-center align-items-center">
                  <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                    Loading...
                  </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    {/* <CommonTable
                      data={DifficultyLevel}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      hasAction={true}
                      permission={permission}
                    /> */}
                    <CommonDataTable
                      data={DifficultyLevel}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
       <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={AddCommonForm}
        FormBodyProps={{ onTranslateClickHandler, translationLoading, lastTranslatedField }} 
        title={isAddOrEdit === "isAdd" ? t("Add New Difficulty Level") : t("Edit Difficulty Level")} 
        size="lg"
      />


      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={handleSave}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={`Are you sure you want to delete this record?`}
      />
    </div>
  );
}

export default DifficultyLevel;